import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, Link } from 'react-router-dom';
import { ChevronDownIcon, PlusIcon, LogOutIcon, User2, X, Users2Icon, TruckIcon, UserCog, Package } from 'lucide-react';
import { useAuth } from '../utils/authContext';
import { FaEllipsisH } from 'react-icons/fa';

const isPWA = () => {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
};

const Sidebar = ({ isMobile = false, isPwa = false }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const { user, catalogs, logout } = useAuth();
    const isPwaMode = isPWA();

    const userData = useSelector((state) => state.user.userData);
    // const selectedCatalog = useSelector((state) => state.user.selectedCatalog);
    const selectedCatalog = user.selectedCatalog || catalogs[0]

    const mainNavItems = [
        { to: "/overview", icon: "home-icon.svg", label: "Overview", category: "Core" },
        { to: "/catalog", icon: "catalog-icon.svg", label: "Catalog", category: "Core" },
        { to: "/personalization", icon: "personalization-icon.svg", label: "Personalization", category: "Core" },
        { to: "/statistics", icon: "statistics-icon.svg", label: "Statistics", category: "Core" },
    ];

    const managementNavItems = [
        { to: "/transactions", icon: "transaction.svg", label: "Orders", category: "Management" },
        { to: "/inventory", icon: "", label: "Inventory", customIcon: <Package className="w-6 h-6" />, category: "Management" },
        // { to: "/customers", icon: "users.svg", label: "Customers", customIcon: <Users2Icon className="w-6 h-6" />, category: "Management" },
        { to: "/payment-settings", icon: "bank-card.svg", label: "Payments", category: "Management" },
        { to: "/shipping-settings", icon: "product.svg", label: "Shipping", customIcon: <TruckIcon className="w-6 h-6" />, category: "Management" },
        { to: "/help-support", icon: "help-icon.svg", label: "Support", category: "Management" },
        { to: "/profile", icon: "user.svg", label: "Account", customIcon: <UserCog className="w-6 h-6" />, category: "Management" },
    ];

    // Combine navigation items for desktop view
    const desktopNavItems = [...mainNavItems, ...managementNavItems];

    const renderNavItem = (item, inDrawer = false) => (
        <NavLink
            to={item.to}
            className={({ isActive }) =>
                `flex ${inDrawer ? 'flex-row p-4' : 'flex-col lg:flex-row'} items-center ${!inDrawer ? 'justify-center lg:justify-start' : ''
                } ${isPwa && isMobile && !inDrawer ? 'pb-4' : ''} ${!inDrawer ? 'p-2 lg:px-4 lg:py-3' : ''
                } rounded-md ${isActive
                    ? 'bg-gray-100 text-[#191970] font-bold'
                    : `${inDrawer ? 'text-gray-800' : 'text-gray-700'} hover:text-[#191970]`
                }`
            }
            onClick={() => inDrawer && setIsDrawerOpen(false)}
            style={({ isActive }) => ({
                '--icon-color': isActive ? '#191970' : inDrawer ? '#374151' : '#404245'
            })}
        >
            {({ isActive }) => (
                <>
                    {item.customIcon ? (
                        <div className={`${inDrawer ? 'mr-6' : 'mb-1 lg:mb-0 lg:mr-3'}`}>
                            {React.cloneElement(item.customIcon, {
                                className: `${item.customIcon.props.className} ${isActive ? 'text-[#191970]' : 'text-gray-700'}`
                            })}
                        </div>
                    ) : (
                        <div
                            className={`w-6 h-6 lg:w-5 lg:h-5 ${!inDrawer ? 'mb-1 lg:mb-0' : ''} ${inDrawer ? 'mr-6' : 'lg:mr-3'}`}
                            style={{
                                maskImage: `url(/icons/${item.icon})`,
                                maskSize: 'contain',
                                WebkitMaskImage: `url(/icons/${item.icon})`,
                                WebkitMaskSize: 'contain',
                                backgroundColor: 'var(--icon-color)',
                            }}
                        />
                    )}
                    <span className={`${!inDrawer ? 'text-xs' : 'text-sm'} lg:flex-1 font-medium`}>{item.label}</span>
                </>
            )}
        </NavLink>
    );

    return (
        <>
            <div className={`bg-white shadow-xl text-gray-800 ${isMobile ? 'w-screen' : 'lg:w-56 lg:fixed lg:h-screen'
                } flex flex-col`}>
                <div className="hidden lg:block p-4 "><a href='/'>
                    <img
                        src="../pricilogFull.svg"
                        alt="Pricilog Logo"
                        className="w-1/3 mb-8 mt-4 mx-auto"
                    />
                </a>


                    {/* Catalog switching */}
                    <div className="hidden lg:block p-1 mb-2 relative">

                        <div className='flex justify-end'>
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="flex p-0.5 w-full items-center bg-white font-bold text-[#191970] hover:border-[#191970]"
                            >
                                <div className="flex items-center justify-between w-11/12 text-xs font-bold text-[#191970] hover:text-[#191970]">
                                    <img
                                        src={selectedCatalog?.logo || '../logo-placeholder.svg'}
                                        alt="Catalog logo"
                                        className="w-10 h-10 rounded-full mr-3"
                                    />
                                    <span className="text-xs text-[#191970]">{selectedCatalog?.catalogName}</span>
                                    <ChevronDownIcon className='' />
                                </div>
                            </button>


                            {isDropdownOpen && (
                                <div className="absolute top-full w-48 mt-2 bg-white rounded-lg shadow-lg z-10">
                                    <NavLink
                                        to="/profile"
                                        className="flex items-center p-3 hover:bg-gray-100 text-[#191970]"
                                        onClick={() => setIsDropdownOpen(false)}
                                    >
                                        <div className="w-6 h-6 rounded-full bg-[#191970] flex items-center justify-center mr-3">
                                            <User2 className="w-5 h-5 text-white" />
                                        </div>
                                        <span className='text-xs font-bold text-[#191970]'>Profile</span>
                                    </NavLink>

                                    <NavLink
                                        to="/login"
                                        className="flex items-center p-3 hover:bg-gray-100 text-blue-600"
                                        onClick={() => {
                                            setIsDropdownOpen(false);
                                            logout();
                                        }
                                        }
                                    >
                                        <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center mr-3 text-white">
                                            <LogOutIcon className="w-5 h-5" />
                                        </div>
                                        <span className='text-red-500 text-xs font-bold'>Logout</span>
                                    </NavLink>
                                </div>
                            )}
                        </div>
                    </div>
                </div>


                <nav className={`flex-1 font-black lg:p-6 ${isMobile ? 'mobile-nav' : 'navigation-menu'}`}>
                    <ul className={`flex lg:flex-col lg:space-y-5`}>
                        {isMobile ? (
                            <nav className="flex-1 px-3 py-2 bg-white shadow-lg rounded-t-xl">
                                <ul className="flex justify-between">
                                    {mainNavItems.map((item) => (
                                        <li key={item.to} className="flex-1">
                                            {renderNavItem(item)}
                                        </li>
                                    ))}
                                    <li className="flex-1">
                                        <button
                                            onClick={() => setIsDrawerOpen(true)}
                                            className="w-full flex flex-col items-center justify-center p-2 text-xs text-gray-500 bg-transparent"
                                        >
                                            <FaEllipsisH className="w-5 h-5 mb-1 text-gray-500" />
                                            <span>More</span>
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        ) : (
                            // Desktop navigation with combined items
                            desktopNavItems.map((item) => (
                                <li key={item.to} className="flex-1 lg:flex-none">
                                    {renderNavItem(item)}
                                </li>
                            ))
                        )}
                    </ul>
                </nav>
            </div>


            {/* Mobile Drawer */}
            {/* Mobile Drawer */}
            {isDrawerOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 backdrop-blur-sm" onClick={() => setIsDrawerOpen(false)}>
                    <div
                        className="fixed inset-y-0 right-0 w-[85%] max-w-sm bg-white shadow-xl flex flex-col transform transition-all duration-300 ease-in-out"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center">
                            <img
                                src={selectedCatalog?.logo || '../logo-placeholder.svg'}
                                alt="Catalog logo"
                                className="w-8 h-8 rounded-full mr-3"
                            />
                            <h3 className="text-lg font-medium text-[#191970] flex-1">{selectedCatalog?.name}</h3>
                            <button
                                onClick={() => setIsDrawerOpen(false)}
                                className="p-2 rounded-full hover:bg-gray-100 bg-transparent"
                            >
                                <X className="w-5 h-5 text-gray-700" />
                            </button>
                        </div>

                        <div className="overflow-y-auto flex-1">
                            <div className="p-2">
                                <div className="mb-4">
                                    <div className="px-3 py-2 text-xs font-semibold bg-gray-50 rounded-md text-gray-700 uppercase tracking-wider">
                                        Core Features
                                    </div>
                                    {mainNavItems.map((item) => (
                                        <div key={item.to} className="my-1">
                                            {renderNavItem(item, true)}
                                        </div>
                                    ))}
                                </div>

                                <div className="mb-4">
                                    <div className="px-3 py-2 text-xs font-semibold bg-gray-50 rounded-md text-gray-700 uppercase tracking-wider">
                                        Management
                                    </div>
                                    {managementNavItems.map((item) => (
                                        <div key={item.to} className="my-1">
                                            {renderNavItem(item, true)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-gray-100 p-3 pb-safe">
                            <button
                                onClick={() => {
                                    setIsDrawerOpen(false);
                                    logout();
                                }}
                                className="flex items-center w-full p-3 rounded-md text-left text-red-600 hover:bg-red-50 transition-colors bg-transparent"
                            >
                                <LogOutIcon className="w-5 h-5 mr-6" />
                                <span className="font-bold">Logout</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Sidebar;